/**
 * Cookie consent and Google Analytics coordination.
 *
 * The canonical GA bootstrap now lives in /public/google-analytics-bootstrap.js
 * so consent defaults are established in <head> before the app bundle runs.
 * This module only handles consent updates, cookie cleanup, and SPA pageviews.
 */

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import { captureAdClickAttribution } from '@/lib/gclidCapture';

type GoogleAnalyticsPageViewOptions = {
  consentGranted?: boolean;
};

export type GoogleConsentState = {
  analyticsGranted: boolean;
  advertisingGranted: boolean;
};

const ANALYTICS_COOKIE_PATTERNS = [/^_ga/, /^_gid/, /^_gat/];
const ADVERTISING_COOKIE_PATTERNS = [/^_gcl/, /^_gac/];

function isGoogleAnalyticsDebugEnabled(): boolean {
  return window.__fabGaDebugEnabled === true || new URLSearchParams(window.location.search).get('ga_debug') === '1';
}

function logGoogleAnalyticsDebug(message: string, payload?: unknown) {
  if (!isGoogleAnalyticsDebugEnabled()) {
    return;
  }

  if (payload === undefined) {
    console.info(`[FAB][GA] ${message}`);
    return;
  }

  console.info(`[FAB][GA] ${message}`, payload);
}

function getGoogleTag(): ((...args: unknown[]) => void) | null {
  if (typeof window.gtag !== 'function') {
    logGoogleAnalyticsDebug('Google tag bootstrap is missing; skipping analytics call');
    return null;
  }

  return window.gtag;
}

function isGoogleAnalyticsConsentGranted(): boolean {
  return window.__fabGaAnalyticsConsentGranted === true;
}

function normalizeGoogleAdsId(googleAdsId?: string): string | null {
  if (typeof googleAdsId !== 'string') {
    return null;
  }

  const trimmedGoogleAdsId = googleAdsId.trim();
  if (!trimmedGoogleAdsId || !trimmedGoogleAdsId.startsWith('AW-')) {
    return null;
  }

  return trimmedGoogleAdsId;
}

function clearMatchingCookies(patterns: RegExp[]) {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const name = cookie.split('=')[0].trim();
    if (!patterns.some((pattern) => pattern.test(name))) {
      continue;
    }

    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}

export function sendGoogleAnalyticsPageView(options: GoogleAnalyticsPageViewOptions = {}): boolean {
  const gtag = getGoogleTag();
  const consentGranted = options.consentGranted ?? isGoogleAnalyticsConsentGranted();
  if (!gtag || !consentGranted) {
    return false;
  }

  const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.__fabLastTrackedGaPage === pagePath) {
    return false;
  }

  gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
  logGoogleAnalyticsDebug('Sent page_view', { pagePath, pageLocation: window.location.href });
  window.__fabLastTrackedGaPage = pagePath;

  return true;
}

export function configureGoogleAdsTag(googleAdsId?: string): boolean {
  const normalizedGoogleAdsId = normalizeGoogleAdsId(googleAdsId);
  if (!normalizedGoogleAdsId) {
    logGoogleAnalyticsDebug('Google Ads tag not configured');
    return false;
  }

  if (window.__fabGoogleAdsTagConfigured === normalizedGoogleAdsId) {
    return false;
  }

  const gtag = getGoogleTag();
  if (!gtag) {
    return false;
  }

  // Do NOT hard-disable personalization signals here. Whether personalization
  // is allowed is governed at runtime by Consent Mode (`ad_personalization`),
  // set from the user's advertising choice in applyGoogleConsentState().
  // Forcing this to false previously suppressed remarketing audiences and
  // Customer Match even after the user accepted advertising cookies.
  gtag('config', normalizedGoogleAdsId, {
    allow_ad_personalization_signals: true,
  });
  logGoogleAnalyticsDebug('Configured Google Ads tag', {
    googleAdsId: normalizedGoogleAdsId,
    allow_ad_personalization_signals: true,
  });
  window.__fabGoogleAdsTagConfigured = normalizedGoogleAdsId;

  return true;
}

/**
 * Applies Google consent flags, cookie cleanup, and deferred gclid capture.
 * Used by the consent banner and the local analytics verification harness.
 */
export function applyGoogleConsentState(state: GoogleConsentState) {
  const gtag = getGoogleTag();
  if (!gtag) {
    return;
  }

  window.__fabGaAnalyticsConsentGranted = state.analyticsGranted;
  window.__fabGaAdvertisingConsentGranted = state.advertisingGranted;
  // ad_personalization follows the advertising choice. There is no separate
  // personalization toggle in the banner, so accepting advertising cookies
  // ("Accept all" or the Advertising category) grants it; rejecting keeps it
  // denied. Leaving this permanently denied disabled remarketing audiences
  // and Customer Match even for users who accepted everything.
  const adConsent = state.advertisingGranted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: state.analyticsGranted ? 'granted' : 'denied',
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: adConsent,
  });
  logGoogleAnalyticsDebug('Updated consent state', {
    analytics_storage: state.analyticsGranted ? 'granted' : 'denied',
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: adConsent,
  });

  if (!state.analyticsGranted) {
    delete window.__fabLastTrackedGaPage;
    clearMatchingCookies(ANALYTICS_COOKIE_PATTERNS);
  }

  if (!state.advertisingGranted) {
    clearMatchingCookies(ADVERTISING_COOKIE_PATTERNS);
  }

  if (state.analyticsGranted) {
    // The consent callback already tells us analytics was chosen, so do not
    // depend on the consent library's persisted state having updated yet.
    sendGoogleAnalyticsPageView({ consentGranted: true });
  }

  if (state.advertisingGranted) {
    // Now that ad cookies are allowed, persist any gclid/utm from the landing
    // URL. No-ops if nothing to capture or already persisted.
    captureAdClickAttribution();
  }
}

function syncGoogleConsent(cookieCategories: string[]) {
  logGoogleAnalyticsDebug('Processing cookie consent categories', { cookieCategories });

  applyGoogleConsentState({
    analyticsGranted: cookieCategories.includes('analytics'),
    advertisingGranted: cookieCategories.includes('advertising'),
  });
}

export function initCookieConsent() {
  logGoogleAnalyticsDebug('Initializing cookie consent UI');

  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom right',
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: 'box',
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        autoClear: {
          cookies: ANALYTICS_COOKIE_PATTERNS.map((pattern) => ({ name: pattern })),
        },
      },
      advertising: {
        enabled: false,
        autoClear: {
          cookies: ADVERTISING_COOKIE_PATTERNS.map((pattern) => ({ name: pattern })),
        },
      },
    },

    onFirstConsent: ({ cookie }) => {
      syncGoogleConsent(cookie.categories);
    },

    onChange: ({ cookie }) => {
      syncGoogleConsent(cookie.categories);
    },

    onConsent: ({ cookie }) => {
      syncGoogleConsent(cookie.categories);
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Cookie & Tracking Preferences',
            description: 'We use essential cookies to keep FAB secure and working. With your permission, we also use analytics cookies to understand site usage and advertising cookies to measure the performance of our marketing and Google Ads campaigns.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject non-essential',
            showPreferencesBtn: 'Manage preferences',
          },
          preferencesModal: {
            title: 'Cookie & Tracking Preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject non-essential',
            savePreferencesBtn: 'Save preferences',
            sections: [
              {
                title: 'Essential Cookies',
                description: 'These cookies are required for the app to function (authentication, session management). They cannot be disabled.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics Cookies',
                description: 'These cookies help us understand how visitors use FAB so we can improve the product. We use Google Analytics for this measurement.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Advertising & Conversion Measurement Cookies',
                description: 'These cookies help us measure ad performance and attribute visits or sign-ups to campaigns, including Google Ads conversion measurement, remarketing audiences, and Customer Match. When enabled, Google may receive identifiers and site activity used to measure and personalize advertising.',
                linkedCategory: 'advertising',
              },
            ],
          },
        },
      },
    },
  });

  logGoogleAnalyticsDebug('Cookie consent UI initialized');
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __fabLastTrackedGaPage?: string;
    __fabGaAnalyticsConsentGranted?: boolean;
    __fabGaAdvertisingConsentGranted?: boolean;
    __fabGoogleAdsTagConfigured?: string;
    __fabGaDebugEnabled?: boolean;
  }
}
